import { getCreditCardType } from 'cleave-zen';
import axios from 'smg/axios';
import * as models from 'smg/customer-api/models';
import { DeletePaymentOption, RestorePaymentOption } from 'smg/customer-api/paymentoptions';
import { Session } from 'smg/customer-api/session';
import Modal from 'smg/modal';
import * as phone from 'smg/phone';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { Success, Undo } from 'smg/toast';
import { CapitalizeFirstLetter } from 'smg/util';
import { AccountDetailsInit, PageLoader, RequireSession } from '../../account';
import { NewPaymentOptionForm } from '../../payments';

interface pageLoader {
    pageLoader: PageLoader<models.HyejooPaymentOption>;
    parent: HTMLElement;
}

void main();

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();

    QueryString().delete('p');

    let $defaultPaymentMethodEl: HTMLElement | undefined;
    const pageLoaders: pageLoader[] = [];
    const $fullPageSpinner = document.querySelector<HTMLElement>('.full-page-spinner');
    const $contentContainer = document.querySelector<HTMLElement>('.content-container');

    // ----- CC Methods ------

    const $savedCardsContainer = document.querySelector<HTMLElement>('.saved-cards-container');
    const $savedCardsCardContainer = $savedCardsContainer?.querySelector<HTMLElement>('.saved-cards-card-container');
    const $noSavedCardsMsg = $savedCardsContainer?.querySelector<HTMLElement>('.no-saved-cards');
    const $cardsBtnContainer = $savedCardsContainer?.querySelector<HTMLElement>('.btn-container');
    const $viewMoreCardsBtn = $cardsBtnContainer?.querySelector<HTMLButtonElement>('#view-more-cards-btn');
    const $showingCardsCount = $cardsBtnContainer?.querySelector<HTMLElement>('#showing-cards-count');

    let cardsNextPage: boolean = true;
    let cardCount: number = 0;

    const cardPl: PageLoader<models.HyejooPaymentOption> = new PageLoader<models.HyejooPaymentOption>({
        clearResults: false,
        parent: $savedCardsCardContainer as HTMLElement,
        spinner: $savedCardsContainer?.querySelector('.card-spinner') as HTMLElement,
        nextBtn: $viewMoreCardsBtn as HTMLButtonElement,
        beforeLoad: async () => {
            if (!await getDefaultPaymentOption()) {
                $defaultPaymentMethodEl = undefined;
            }
            const currHeight = $savedCardsContainer?.offsetHeight;
            if (currHeight) {
                $savedCardsContainer.style.height = `${currHeight}px`;
            }
        },
        get: () => axios.get('/payment-options', {
            params: {
                'sort': 'default desc,created desc',
                'data': 'address,creditCardFirst6,creditCardLast4,creditCardExpirationDate,key,paymentOptionTypeID,label',
                'q': `valid:true has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip type:${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT}`,
                'max': '5',
                'p': cardPl.currentPage(),
            },
        }).then(resp => {
            const data = models.PaymentOptionsHyejooResponse.fromJSON(resp.data);
            cardCount = data.paymentOptions.length;
            cardsNextPage = data.nextPage;
            return { rows: data.paymentOptions, nextPage: data.nextPage };
        },
        ),
        draw: paymentOption => {
            const $el = template.Clone('#payment-option-card-cc').firstElementChild as HTMLElement;
            const $input = $el.querySelector<HTMLInputElement>('input');
            const $label = $el.querySelector<HTMLLabelElement>('label');
            if ($input && $label && paymentOption.id) {
                $input.value = paymentOption.id;
                $input.id = `po-id-${paymentOption.id}`;
                $input.name = 'payment-option-radio';
                $label.htmlFor = `po-id-${paymentOption.id}`;
            }
            const $ccLastFour = $el.querySelector('.cc-last-four') as HTMLElement;
            const $ccIssuer = $el.querySelector('.cc-issuer') as HTMLElement;
            const $address = $el.querySelector('.cc-address') as HTMLElement;
            $address.textContent = paymentOption.address.toLocalityLine();
            // Checkout drops options whose stored phone fails the server tel validator; let the customer fix it here.
            if (!phone.IsValid(paymentOption.address.phoneNumber?.phone)) {
                $address.insertAdjacentElement('afterend', phone.InvalidPhoneNote());
            }
            if (paymentOption.creditCardLast4) {
                $ccLastFour.textContent = paymentOption.creditCardLast4.toString().padStart(4, '0');
            }
            if (paymentOption.creditCardFirst6) {
                switch (getCreditCardType(paymentOption.creditCardFirst6.toString()).toString()) {
                case 'amex':
                    $ccIssuer.textContent = 'Amex';
                    $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                    $el.querySelector('.cc-icon-amex')?.classList.remove('d-none');
                    break;
                case 'discover':
                    $ccIssuer.textContent = 'Discover';
                    $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                    $el.querySelector('.cc-icon-discover')?.classList.remove('d-none');
                    break;
                case 'mastercard':
                    $ccIssuer.textContent = 'Mastercard';
                    $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                    $el.querySelector('.cc-icon-mastercard')?.classList.remove('d-none');
                    break;
                case 'visa':
                    $ccIssuer.textContent = 'Visa';
                    $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                    $el.querySelector('.cc-icon-visa')?.classList.remove('d-none');
                    break;
                }
            }

            const $edit = $el.querySelector<HTMLElement>('.payment-option-edit');
            const $delete = $el.querySelector<HTMLElement>('.payment-option-delete');
            $edit?.addEventListener('click', async () => {
                await NewPaymentOptionForm((await Session()).user, 'credit-debit', undefined, paymentOption).then(() => {
                    QueryString().delete('p');
                    if ($savedCardsCardContainer) {
                        $savedCardsCardContainer.innerHTML = '';
                    }
                    cardPl.load();
                });
            });
            $delete?.addEventListener('click', () => {
                const cardType = getCreditCardType(paymentOption.creditCardFirst6.toString()).toString();
                const cardTypeCapitalized = CapitalizeFirstLetter(cardType);
                const $body = document.createElement('div');
                $body.classList.add('text-center');
                const last4 = paymentOption.creditCardLast4 ?
                    `(...${paymentOption.creditCardLast4.toString().padStart(4, '0')})?` :
                    '';
                $body.innerHTML = `<p class="mb-0">Are you sure you want to delete <span class="fw-bold">${cardTypeCapitalized}</span> ending in ${last4}</p>`;
                const $m = new Modal({
                    title: 'Confirm Delete!',
                    body: $body,
                    headerLevel: 'h2',
                    size: 'modal-sm',
                    cancelButtonClass: 'd-none',
                    confirmText: 'Delete',
                    onSubmit: async () => {
                        if (paymentOption.id) {
                            await DeletePaymentOption(paymentOption.id).then(() => {
                                Undo('Removed payment method.', async () => {
                                    await RestorePaymentOption(paymentOption.id);
                                    cardPl.setPage(0);
                                    if ($savedCardsCardContainer) {
                                        $savedCardsCardContainer.innerHTML = '';
                                    }
                                    cardPl.load();
                                    Success('Payment method restored.');
                                });
                                cardPl.setPage(0);
                                if ($savedCardsCardContainer) {
                                    $savedCardsCardContainer.innerHTML = '';
                                }
                                if (paymentOption.label === 'default') {
                                    $defaultPaymentMethodEl = undefined;
                                }
                                cardPl.load();
                            });
                        }
                    },
                });
                const $modalBody = $m.$element.querySelector<HTMLElement>('.modal-body');
                if ($modalBody) {
                    $modalBody.classList.add('py-0');
                }
            });

            if (paymentOption.label === 'default') {
                $defaultPaymentMethodEl = $el;
            } else {
                createMakeDefaultBtn($el, paymentOption);
            }

            return $el;
        },
        afterLoad: async () => {
            if (!(cardCount > 0)) {
                $noSavedCardsMsg?.classList.remove('d-none');
            } else {
                $noSavedCardsMsg?.classList.add('d-none');
                if (cardCount * (cardPl.currentPage() + 1) > 4) {
                    if ($showingCardsCount) {
                        $showingCardsCount.textContent = `(showing ${cardCount * (cardPl.currentPage() + 1)} of ${await getCardCount()})`;
                    }
                }
            }

            $cardsBtnContainer?.classList.remove('d-none');

            if (cardsNextPage) {
                $viewMoreCardsBtn?.classList.remove('d-none');
            } else {
                $viewMoreCardsBtn?.classList.add('d-none');
            }

            if ($savedCardsContainer) {
                $savedCardsContainer.style.height = 'auto';
            }

            setDefaultPaymentMethod($defaultPaymentMethodEl);
        },
    });

    // ----- Bank Methods ------

    const $bankAccountsContainer = document.querySelector<HTMLElement>('.bank-accounts-container');
    const $bankAccountsCardContainer = $bankAccountsContainer?.querySelector<HTMLElement>('.bank-accounts-card-container');
    const $noBankAccountsMsg = $bankAccountsContainer?.querySelector<HTMLElement>('.no-bank-accounts');
    const $banksBtnContainer = $bankAccountsContainer?.querySelector<HTMLElement>('.btn-container');
    const $viewMoreBanksBtn = $banksBtnContainer?.querySelector<HTMLButtonElement>('#view-more-banks-btn');
    const $showingBanksCount = $banksBtnContainer?.querySelector<HTMLElement>('#showing-banks-count');

    let banksNextPage: boolean = true;
    let bankCount: number = 0;

    const bankPl: PageLoader<models.HyejooPaymentOption> = new PageLoader<models.HyejooPaymentOption>({
        clearResults: false,
        parent: $bankAccountsCardContainer as HTMLElement,
        spinner: $bankAccountsContainer?.querySelector('.bank-spinner') as HTMLElement,
        nextBtn: $viewMoreBanksBtn as HTMLButtonElement,
        beforeLoad: async () => {
            if (!await getDefaultPaymentOption()) {
                $defaultPaymentMethodEl = undefined;
            }
            const currHeight = $bankAccountsContainer?.offsetHeight;
            if (currHeight) {
                $bankAccountsContainer.style.height = `${currHeight}px`;
            }
        },
        get: () => axios.get('/payment-options', {
            params: {
                'sort': 'default desc,created desc',
                'data': 'address,' +
                    'creditCardFirst6,creditCardLast4,creditCardExpirationDate,key,paymentOptionTypeID,label,accountType,bankType',
                'q': `valid:true has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip type:${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK}`,
                'max': 5,
                'p': bankPl.currentPage(),
            },
        }).then(resp => {
            const data = models.PaymentOptionsHyejooResponse.fromJSON(resp.data);
            bankCount = data.paymentOptions.length;
            banksNextPage = data.nextPage;
            return { rows: data.paymentOptions, nextPage: data.nextPage };
        },
        ),
        draw: paymentOption => {
            const $el = template.Clone('#payment-option-card-bank').firstElementChild as HTMLElement;
            const $input = $el.querySelector<HTMLInputElement>('input');
            const $label = $el.querySelector<HTMLLabelElement>('label');
            if ($input && $label && paymentOption.id) {
                $input.value = paymentOption.id;
                $input.id = `po-id-${paymentOption.id}`;
                $input.name = 'payment-option-radio';
                $label.htmlFor = `po-id-${paymentOption.id}`;
            }

            const bankType = $el.querySelector('.bank-acct-type') as HTMLElement;
            if (paymentOption.bankType && paymentOption.accountType) {
                bankType.textContent = `${CapitalizeFirstLetter(paymentOption.bankType)} ${CapitalizeFirstLetter(paymentOption.accountType)} Account`;
                if (paymentOption.creditCardLast4) {
                    bankType.textContent += ` (...${paymentOption.creditCardLast4})`;
                }
            }
            const $address = $el.querySelector('.payment-option-address') as HTMLElement;
            $address.textContent = paymentOption.address.toLocalityLine();
            // Checkout drops options whose stored phone fails the server tel validator; let the customer fix it here.
            if (!phone.IsValid(paymentOption.address.phoneNumber?.phone)) {
                $address.insertAdjacentElement('afterend', phone.InvalidPhoneNote());
            }

            const $edit = $el.querySelector<HTMLElement>('.payment-option-edit');
            const $delete = $el.querySelector<HTMLElement>('.payment-option-delete');
            $edit?.addEventListener('click', async () => {
                await NewPaymentOptionForm((await Session()).user, 'bank', undefined, paymentOption).then(() => {
                    QueryString().delete('p');
                    if ($bankAccountsCardContainer) {
                        $bankAccountsCardContainer.innerHTML = '';
                    }
                    bankPl.load();
                });
            });
            $delete?.addEventListener('click', () => {
                const $body = document.createElement('div');
                $body.classList.add('text-center');
                const accountLabel = paymentOption.accountType ? paymentOption.accountType : 'bank';
                const last4 = paymentOption.creditCardLast4 ? `(...${paymentOption.creditCardLast4})?` : '';
                $body.textContent = `Are you sure you want to delete ${accountLabel} account ${last4}`;
                const $m = new Modal({
                    title: 'Confirm Delete!',
                    body: $body,
                    headerLevel: 'h2',
                    size: 'modal-sm',
                    cancelButtonClass: 'd-none',
                    confirmText: 'Delete',
                    onSubmit: async () => {
                        if (paymentOption.id) {
                            await DeletePaymentOption(paymentOption.id).then(() => {
                                Undo('Removed payment method.', async () => {
                                    await RestorePaymentOption(paymentOption.id);
                                    bankPl.setPage(0);
                                    if ($bankAccountsCardContainer) {
                                        $bankAccountsCardContainer.innerHTML = '';
                                    }
                                    bankPl.load();
                                    Success('Payment method restored.');
                                });
                                bankPl.setPage(0);
                                if ($bankAccountsCardContainer) {
                                    $bankAccountsCardContainer.innerHTML = '';
                                }
                                if (paymentOption.label === 'default') {
                                    $defaultPaymentMethodEl = undefined;
                                }
                                bankPl.load();
                            });
                        }
                    },
                });

                const $modalBody = $m.$element.querySelector<HTMLElement>('.modal-body');
                if ($modalBody) {
                    $modalBody.classList.add('py-0');
                }
            });

            if (paymentOption.label === 'default') {
                $defaultPaymentMethodEl = $el;
            } else {
                createMakeDefaultBtn($el, paymentOption);
            }

            return $el;
        },
        afterLoad: async () => {
            if (!(bankCount > 0)) {
                $noBankAccountsMsg?.classList.remove('d-none');
            } else {
                $noBankAccountsMsg?.classList.add('d-none');
                if (bankCount * (bankPl.currentPage() + 1) > 4) {
                    if ($showingBanksCount) {
                        $showingBanksCount.textContent = `(showing ${bankCount * (bankPl.currentPage() + 1)} of ${await getBankCount()})`;
                    }
                }
            }

            $banksBtnContainer?.classList.remove('d-none');

            if (banksNextPage) {
                $viewMoreBanksBtn?.classList.remove('d-none');
            } else {
                $viewMoreBanksBtn?.classList.add('d-none');
            }

            if ($bankAccountsContainer) {
                $bankAccountsContainer.style.height = 'auto';
            }

            setDefaultPaymentMethod($defaultPaymentMethodEl);
        },
    });

    pageLoaders.push({
        pageLoader: cardPl,
        parent: $savedCardsCardContainer as HTMLElement,
    }, {
        pageLoader: bankPl,
        parent: $bankAccountsCardContainer as HTMLElement,
    });

    cardPl.load();
    bankPl.load();

    const $addNewCCBtn = document.querySelector<HTMLElement>('#add-new-card-btn');
    const $addNewBankBtn = document.querySelector<HTMLElement>('#add-new-bank-btn');

    [$addNewCCBtn, $addNewBankBtn].forEach($btn => {
        $btn?.addEventListener('click', async e => {
            await NewPaymentOptionForm((await Session()).user, e.target === $addNewCCBtn ? 'credit-debit' : 'bank').then(() => {
                if (e.target === $addNewCCBtn) {
                    cardPl.setPage(0);
                    if ($savedCardsCardContainer) {
                        $savedCardsCardContainer.innerHTML = '';
                    }
                    cardPl.load();
                } else {
                    bankPl.setPage(0);
                    if ($bankAccountsCardContainer) {
                        $bankAccountsCardContainer.innerHTML = '';
                    }
                    bankPl.load();
                }
            });
        });
    });

    function createMakeDefaultBtn($card: HTMLElement, paymentOption: models.HyejooPaymentOption) {
        const $wrapper = document.createElement('div');
        $wrapper.classList.add('w-100', 'mt-4', 'make-default-wrapper');

        const $btn = document.createElement('a');
        $btn.classList.add('text-decoration-underline', 'small');
        $btn.href = '#';
        $btn.textContent = 'Make Default';

        $wrapper.appendChild($btn);

        const $label = $card.querySelector<HTMLElement>('.form-check-label');
        if ($label) {
            $label.appendChild($wrapper);
        }

        $btn.addEventListener('click', async e => {
            e.preventDefault();
            if (paymentOption.id) {
                await axios.patch(`/payment-options/${paymentOption.id}`, {
                    label: 'default',
                }).then(() => {
                    for (const pl of pageLoaders) {
                        pl.pageLoader.setPage(0);
                        pl.parent.innerHTML = '';
                        pl.pageLoader.load();
                    }
                });
            }
        });
    }

    $fullPageSpinner?.classList.add('d-none');
    $contentContainer?.classList.remove('d-none');
}

function setDefaultPaymentMethod($card: HTMLElement | undefined) {
    const $defaultPaymentMethodContainer = document.querySelector<HTMLElement>('.default-payment-container');
    const $defaultPaymentMethodCardContainer = $defaultPaymentMethodContainer?.querySelector<HTMLElement>('.default-payment-card-container');
    const $noDefaultPaymentMsg = $defaultPaymentMethodContainer?.querySelector<HTMLElement>('.no-default-payment');

    if ($defaultPaymentMethodCardContainer) {
        if (!$card) {
            $defaultPaymentMethodCardContainer.innerHTML = '';
            $noDefaultPaymentMsg?.classList.remove('d-none');
            return;
        } else {
            $noDefaultPaymentMsg?.classList.add('d-none');
            $defaultPaymentMethodCardContainer.innerHTML = '';
            $defaultPaymentMethodCardContainer.appendChild($card);
            const $defaultTagWrapper = $defaultPaymentMethodCardContainer.querySelector<HTMLElement>('.default-tag-wrapper');
            if ($defaultTagWrapper) {
                return;
            }
            const $wrapper = document.createElement('div');
            $wrapper.classList.add('default-tag-wrapper', 'mt-4', 'd-inline-block');
            const $defaultTag = document.createElement('span');
            $defaultTag.classList.add('tag', 'mb-0');
            $defaultTag.textContent = 'Your Default';
            $wrapper.appendChild($defaultTag);
            const $label = $defaultPaymentMethodCardContainer.querySelector<HTMLElement>('.form-check-label');
            if ($label) {
                $label.appendChild($wrapper);
            }
        }
    }
}

async function getCardCount(): Promise<number> {
    const resp = await axios.get('/payment-options', {
        params: {
            'colStats': 'id',
            'q': `valid:true has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip type:${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT}`,
        },
    });
    return models.ColumnStatsResponse.fromJSON(resp.data).count;
}

async function getBankCount(): Promise<number> {
    const resp = await axios.get('/payment-options', {
        params: {
            'colStats': 'id',
            'q': `valid:true has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip type:${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK}`,
        },
    });
    return models.ColumnStatsResponse.fromJSON(resp.data).count;
}

async function getDefaultPaymentOption(): Promise<models.HyejooPaymentOption | undefined> {
    const resp = await axios.get('/payment-options', {
        params: {
            'sort': 'default desc',
            'data': 'id,label',
            'q': 'valid:true has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip label:default',
            'max': 1,
        },
    });
    const data = models.PaymentOptionsHyejooResponse.fromJSON(resp.data);
    return data.paymentOptions[0];
}
